<script setup>
import { useI18n } from 'vue-i18n';
import julianaAvatar from 'dashboard/assets/images/auth/onboarding/juliana.webp';
import biancaAvatar from 'dashboard/assets/images/auth/onboarding/bianca.webp';
import felipeAvatar from 'dashboard/assets/images/auth/onboarding/felipe.webp';
import marinaAvatar from 'dashboard/assets/images/auth/onboarding/marina.webp';
import camilaAvatar from 'dashboard/assets/images/auth/onboarding/camila.webp';

const props = defineProps({ step: { type: Number, required: true } });
const { t } = useI18n();
const text = key => t(`REGISTER.GUIDED.PREVIEW.STEP_${props.step}.${key}`);
const contactAvatars = [julianaAvatar, biancaAvatar, felipeAvatar];
const channelIcons = [
  'i-ri-whatsapp-line text-green-700',
  'i-lucide-instagram text-violet-500',
  'i-lucide-mail text-woot-800',
];
const navigationIcons = [
  'i-lucide-inbox',
  'i-lucide-users-round',
  'i-lucide-chart-no-axes-combined',
  'i-lucide-settings-2',
];
const trafficBars = ['h-8', 'h-14', 'h-10', 'h-20', 'h-16', 'h-24', 'h-14'];
</script>

<template>
  <aside
    class="mx-auto w-full min-w-0 max-w-[560px] rounded-[28px] border border-slate-100/70 p-5 sm:p-6 lg:mx-0"
    :class="
      step === 2 || step === 4
        ? 'bg-gradient-to-br from-woot-25 via-woot-50/60 to-white'
        : 'bg-gradient-to-br from-slate-25 via-woot-25/50 to-woot-50'
    "
  >
    <p
      class="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-woot-800"
    >
      <span aria-hidden="true" class="h-px w-5 bg-woot-700" />
      {{ text('EYEBROW') }}
    </p>
    <h2
      class="mb-2 max-w-[420px] text-[27px] font-semibold leading-[1.15] tracking-[-0.035em] text-slate-900 sm:text-[32px]"
    >
      {{ text('TITLE') }}
    </h2>
    <p class="mb-5 max-w-[420px] text-sm leading-relaxed text-slate-600">
      {{ text('DESCRIPTION') }}
    </p>

    <!-- Product illustrations are intentionally non-interactive. The benefit is described above and below. -->
    <div aria-hidden="true" class="select-none">
      <div v-if="step === 1" class="relative pt-2">
        <div class="mb-4 flex justify-center gap-2">
          <span
            v-for="(icon, index) in channelIcons"
            :key="icon"
            class="flex items-center gap-1.5 rounded-full border border-white bg-white/80 px-2.5 py-2 text-[11px] font-medium text-slate-600 shadow-sm"
          >
            <span class="size-4 shrink-0" :class="icon" />
            {{ text(`CHANNEL_${index + 1}`) }}
          </span>
        </div>
        <div
          class="flex overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl shadow-woot-900/5"
        >
          <div
            class="flex w-10 shrink-0 flex-col items-center gap-5 bg-slate-900 py-5 sm:w-12"
          >
            <span class="i-lucide-messages-square mb-3 size-5 text-woot-500" />
            <span
              v-for="(icon, index) in navigationIcons"
              :key="icon"
              :class="[icon, index === 0 ? 'text-woot-500' : 'text-white/50']"
              class="size-4"
            />
          </div>
          <div class="min-w-0 flex-1">
            <div
              class="flex items-center justify-between border-b border-slate-100 px-4 py-4"
            >
              <span class="text-xs font-semibold">
                {{ text('PANEL_TITLE') }}
              </span>
              <span class="i-lucide-search size-4 text-slate-600" />
            </div>
            <div
              v-for="(icon, index) in channelIcons"
              :key="icon"
              class="flex gap-2.5 border-b border-slate-100/60 px-3 py-3.5 last:border-0 sm:px-4"
              :class="{ 'bg-woot-25': index === 0 }"
            >
              <span class="relative size-9 shrink-0">
                <img
                  :src="contactAvatars[index]"
                  alt=""
                  width="36"
                  height="36"
                  class="size-full rounded-full object-cover"
                />
                <span
                  class="absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full bg-white ring-2 ring-white"
                >
                  <span class="size-3" :class="icon" />
                </span>
              </span>
              <div class="min-w-0 flex-1">
                <p
                  class="mb-1 flex items-center justify-between gap-2 text-xs font-semibold text-slate-900"
                >
                  {{ text(`NAME_${index + 1}`) }}
                  <span class="text-[10px] font-normal text-slate-600">
                    {{ text(`TIME_${index + 1}`) }}
                  </span>
                </p>
                <p class="mb-0 text-xs leading-relaxed text-slate-600">
                  {{ text(`REQUEST_${index + 1}`) }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div
          class="relative -mt-2 ml-auto mr-3 flex w-fit max-w-[95%] items-center gap-2 rounded-xl border border-woot-100 bg-white px-3 py-2.5 text-xs font-medium text-slate-900 shadow-lg shadow-woot-900/5"
        >
          <span
            class="flex size-6 shrink-0 items-center justify-center rounded-full bg-woot-100"
          >
            <span class="i-lucide-check-check size-3.5 text-woot-800" />
          </span>
          {{ text('CALLOUT') }}
        </div>
      </div>

      <div v-else-if="step === 2" class="relative pb-2">
        <div
          class="mr-5 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
        >
          <div
            class="mb-4 flex items-center gap-2.5 border-b border-slate-100 pb-3"
          >
            <img
              :src="julianaAvatar"
              alt=""
              width="36"
              height="36"
              class="size-9 shrink-0 rounded-full object-cover"
            />
            <div class="flex-1">
              <p class="mb-0.5 text-xs font-semibold">{{ text('CUSTOMER') }}</p>
              <p
                class="mb-0 flex items-center gap-1 text-[11px] text-slate-600"
              >
                <span class="i-ri-whatsapp-line size-3 text-green-700" />
                {{ text('CHANNEL') }}
              </p>
            </div>
            <span class="i-lucide-ellipsis size-4 text-slate-600" />
          </div>
          <p
            class="mb-0 rounded-xl rounded-tl-sm bg-slate-25 p-3 text-xs leading-relaxed text-slate-900"
          >
            {{ text('QUESTION') }}
          </p>
        </div>
        <div
          class="relative -mt-1 ml-4 rounded-2xl border border-woot-200 bg-white p-4 shadow-xl shadow-woot-900/10 sm:ml-8"
        >
          <p
            class="mb-3 flex items-center gap-2 text-xs font-semibold text-woot-900"
          >
            <span
              class="flex size-7 items-center justify-center rounded-lg bg-woot-500"
            >
              <span class="i-lucide-sparkles size-4" />
            </span>
            {{ text('SUGGESTION') }}
            <span class="ml-auto flex gap-1">
              <span class="size-1 rounded-full bg-woot-600" />
              <span class="size-1 rounded-full bg-woot-300" />
              <span class="size-1 rounded-full bg-woot-100" />
            </span>
          </p>
          <p class="mb-3 text-[13px] leading-relaxed text-slate-900">
            {{ text('ANSWER') }}
          </p>
          <p class="mb-3 flex items-center gap-1.5 text-[11px] text-slate-600">
            <span class="i-lucide-book-open size-3.5 shrink-0" />
            {{ text('SOURCE') }}
          </p>
          <div
            class="flex items-center justify-between border-t border-slate-100 pt-3"
          >
            <span class="flex gap-2 text-slate-600">
              <span class="i-lucide-thumbs-up size-3.5" />
              <span class="i-lucide-thumbs-down size-3.5" />
            </span>
            <span
              class="flex items-center gap-2 rounded-lg bg-woot-500 px-3 py-2 text-[11px] font-semibold text-woot-900"
            >
              {{ text('REVIEW') }}
              <span class="i-lucide-arrow-up-right size-3.5" />
            </span>
          </div>
        </div>
      </div>

      <div v-else-if="step === 3" class="relative pb-2">
        <div
          class="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl shadow-woot-900/5"
        >
          <div class="flex items-center gap-3 bg-slate-900 p-5">
            <img
              :src="marinaAvatar"
              alt=""
              width="48"
              height="48"
              class="size-12 shrink-0 rounded-2xl object-cover"
            />
            <div>
              <p class="mb-1 text-sm font-semibold text-white">
                {{ text('CONTACT') }}
              </p>
              <p class="mb-0 text-xs text-white/70">{{ text('COMPANY') }}</p>
            </div>
            <span class="i-lucide-contact-round ml-auto size-5 text-white/50" />
          </div>
          <div class="p-5">
            <div class="mb-5 flex flex-wrap gap-2">
              <span
                class="rounded-md bg-woot-50 px-2 py-1 text-[10px] font-medium text-woot-800"
              >
                {{ text('TAG_1') }}
              </span>
              <span
                class="rounded-md bg-slate-50 px-2 py-1 text-[10px] font-medium text-slate-600"
              >
                {{ text('TAG_2') }}
              </span>
            </div>
            <p
              class="mb-4 text-[11px] font-semibold uppercase tracking-wider text-slate-600"
            >
              {{ text('PANEL_TITLE') }}
            </p>
            <div
              v-for="entry in 3"
              :key="entry"
              class="relative flex gap-3 pb-3 last:pb-0"
            >
              <div
                v-if="entry < 3"
                class="absolute bottom-0 left-3 top-6 w-px bg-slate-100"
              />
              <span
                class="relative flex size-6 shrink-0 items-center justify-center rounded-full border border-slate-100 bg-slate-25"
              >
                <span
                  class="size-3 text-woot-800"
                  :class="
                    [
                      'i-lucide-instagram',
                      'i-lucide-messages-square',
                      'i-lucide-check',
                    ][entry - 1]
                  "
                />
              </span>
              <div>
                <p class="mb-1 text-xs font-semibold text-slate-900">
                  {{ text(`EVENT_${entry}`) }}
                </p>
                <p class="mb-0 text-xs leading-relaxed text-slate-600">
                  {{ text(`DETAIL_${entry}`) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="step === 4" class="relative pb-2">
        <div
          class="mx-auto w-fit rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm"
        >
          <p class="mb-0 flex items-center gap-2 text-xs font-semibold">
            <span class="i-lucide-messages-square size-4 text-woot-800" />
            {{ text('PANEL_TITLE') }}
          </p>
        </div>
        <div class="mx-auto h-5 w-px bg-woot-200" />
        <div class="grid grid-cols-2 gap-3">
          <div
            v-for="team in 2"
            :key="team"
            class="rounded-xl border p-3 text-center"
            :class="
              team === 1
                ? 'border-woot-200 bg-woot-50'
                : 'border-slate-100 bg-white/70'
            "
          >
            <span
              class="mx-auto mb-2 flex size-8 items-center justify-center rounded-lg"
              :class="
                team === 1
                  ? 'bg-woot-500 text-woot-900'
                  : 'bg-slate-50 text-slate-600'
              "
            >
              <span
                class="size-4"
                :class="team === 1 ? 'i-lucide-handshake' : 'i-lucide-headset'"
              />
            </span>
            <p class="mb-0 text-xs font-medium">{{ text(`TEAM_${team}`) }}</p>
          </div>
        </div>
        <div class="ml-[25%] h-5 w-px bg-woot-200" />
        <div
          class="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl shadow-woot-900/5"
        >
          <div class="flex items-center gap-3 p-4">
            <span class="relative size-10 shrink-0">
              <img
                :src="camilaAvatar"
                alt=""
                width="40"
                height="40"
                class="size-full rounded-full object-cover"
              />
              <span
                class="absolute bottom-0 right-0 size-2.5 rounded-full bg-green-700 ring-2 ring-white"
              />
            </span>
            <div class="flex-1">
              <p class="mb-1 text-xs font-semibold">{{ text('ASSIGNEE') }}</p>
              <p class="mb-0 text-[11px] text-slate-600">
                {{ text('ROUTING') }}
              </p>
            </div>
            <span class="i-lucide-check-check size-4 text-green-700" />
          </div>
          <div
            class="mx-4 mb-4 rounded-xl border border-woot-100 bg-woot-25 p-3.5"
          >
            <p
              class="mb-2 flex items-center gap-1.5 text-[11px] font-semibold text-woot-800"
            >
              <span class="i-lucide-lock-keyhole size-3.5 shrink-0" />
              {{ text('NOTE_LABEL') }}
            </p>
            <p class="mb-0 text-xs leading-relaxed text-slate-900">
              {{ text('NOTE') }}
            </p>
          </div>
        </div>
      </div>

      <div v-else-if="step === 5" class="relative pb-2">
        <div
          class="rounded-2xl border border-slate-100 bg-white p-4 shadow-xl shadow-woot-900/5"
        >
          <div class="mb-4 flex items-center justify-between gap-2">
            <span class="flex items-center gap-2 text-xs font-semibold">
              <span
                class="i-lucide-chart-no-axes-combined size-4 text-woot-800"
              />
              {{ text('PANEL_TITLE') }}
            </span>
            <span
              class="rounded-md border border-slate-100 px-2 py-1 text-[10px] text-slate-600"
            >
              {{ text('PERIOD') }}
            </span>
          </div>
          <div class="mb-4 grid grid-cols-3 gap-2">
            <div
              v-for="(value, index) in ['18', '6', '42']"
              :key="value"
              class="rounded-xl px-3 py-3"
              :class="
                index === 2
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-25 text-slate-900'
              "
            >
              <p class="mb-1 text-[26px] font-semibold tracking-tight">
                {{ value }}
              </p>
              <p
                class="mb-0 text-[10px]"
                :class="index === 2 ? 'text-white/70' : 'text-slate-600'"
              >
                {{ text(`METRIC_${index + 1}`) }}
              </p>
            </div>
          </div>
          <p class="mb-3 text-xs font-medium text-slate-600">
            {{ text('CHART') }}
          </p>
          <div class="relative">
            <div
              class="absolute inset-x-0 top-0 flex h-24 flex-col justify-between"
            >
              <div
                v-for="line in 4"
                :key="line"
                class="border-t border-dashed border-slate-100"
              />
            </div>
            <div
              class="relative flex h-24 items-end justify-between gap-3 px-1"
            >
              <div
                v-for="(height, index) in trafficBars"
                :key="index"
                class="relative flex-1 rounded-t-md"
                :class="[height, index === 5 ? 'bg-woot-500' : 'bg-woot-100']"
              >
                <span
                  v-if="index === 5"
                  class="absolute left-1/2 top-2 -translate-x-1/2 text-[10px] font-semibold text-woot-900"
                >
                  {{ text('PEAK') }}
                </span>
              </div>
            </div>
            <div class="mt-2 flex justify-between gap-3 px-1">
              <span
                v-for="day in 7"
                :key="day"
                class="flex-1 text-center text-[10px] text-slate-600"
              >
                {{ text(`DAY_${day}`) }}
              </span>
            </div>
          </div>
        </div>
        <div
          class="relative -mt-2 ml-3 mr-3 flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-3 text-xs text-slate-600 shadow-lg shadow-woot-900/5"
        >
          <span class="i-lucide-lightbulb size-4 shrink-0 text-woot-800" />
          {{ text('INSIGHT') }}
        </div>
      </div>
    </div>
    <p
      class="mb-0 mt-4 flex items-start gap-2 border-t border-woot-900/10 pt-3 text-xs leading-relaxed text-slate-600"
    >
      <span
        aria-hidden="true"
        class="i-lucide-check size-4 shrink-0 text-woot-800"
      />
      {{ text('BENEFIT') }}
    </p>
  </aside>
</template>
